import React, { useState } from 'react';
import detailMessage from '../detailMessage';
import { useLang } from '../LangContext';

interface Props {
  title: string;
  hint: string;
  min: number;
  max: number;
  step?: number;
  initialValue: number | string;
  saveLabel?: string;
  successMessage: string;
  errorMessage: string;
  validate?: (value: number) => string | null;
  save: (value: number) => Promise<number>;
}

const NumericFieldForm = ({
  title,
  hint,
  min,
  max,
  step,
  initialValue,
  saveLabel,
  successMessage,
  errorMessage,
  validate,
  save,
}: Props) => {
  const { t } = useLang();
  const [value, setValue] = useState<number | string>(initialValue);
  const [savedValue, setSavedValue] = useState<number | string>(initialValue);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    const parsed = parseInt(value as string, 10);
    const validationError = validate ? validate(parsed) : null;
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    try {
      const next = await save(parsed);
      setValue(next);
      setSavedValue(next);
      setSuccess(successMessage);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(detailMessage(err, errorMessage));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="custom-slug-section">
      <h4>{title}</h4>
      <p className="duration-options-hint">{hint}</p>

      {error && <div className="error-message"><strong>{t.errorLabel2}</strong> {error}</div>}
      {success && <div className="success-message"><strong>✓</strong> {success}</div>}

      <form className="custom-slug-form" onSubmit={handleSubmit}>
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => setValue(e.target.value === '' ? '' : Number(e.target.value))}
          disabled={saving}
        />
        <button
          type="submit"
          className="submit-button"
          disabled={saving || value === savedValue}
        >
          {saving ? t.numericSaving : (saveLabel ?? t.saveButton)}
        </button>
      </form>
    </section>
  );
};

export default NumericFieldForm;
